import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, UserPlus, Trash2, Plus, Loader2 } from "lucide-react";

const STANDARDS = [
  { value: "corp", label: "企業会計" },
  { value: "swf", label: "社会福祉法人会計基準" },
  { value: "npo", label: "NPO法人会計" },
  { value: "assoc", label: "一般社団法人等の会計" },
];

export default function Organization() {
  const { user, activeTenantId, refresh } = useAuth();
  const [tab, setTab] = useState("companies");
  const [tenants, setTenants] = useState([]);
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  // create company
  const [newName, setNewName] = useState("");
  const [newStd, setNewStd] = useState("corp");
  // invite member
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invRole, setInvRole] = useState("staff");
  const [invPass, setInvPass] = useState("");
  const [invCaps, setInvCaps] = useState({ accounting: true, billing: true, photo: true });

  const loadTenants = useCallback(async () => {
    const { data } = await api.get("/tenants");
    setTenants(data);
  }, []);

  const loadMembers = useCallback(async () => {
    if (!activeTenantId) return;
    try {
      const [m, r] = await Promise.all([
        api.get(`/tenants/${activeTenantId}/members`),
        api.get("/roles"),
      ]);
      setMembers(m.data);
      setRoles(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [activeTenantId]);

  const loadLogs = useCallback(async () => {
    if (!activeTenantId) return;
    try {
      const { data } = await api.get(`/tenants/${activeTenantId}/audit-logs`);
      setLogs(data);
    } catch (e) {
      setLogs([]);
    }
  }, [activeTenantId]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);
  useEffect(() => {
    if (tab === "members") loadMembers();
    if (tab === "logs") loadLogs();
  }, [tab, loadMembers, loadLogs]);

  const createCompany = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try {
      await api.post("/tenants", { name: newName, accounting_standard: newStd });
      toast.success("会社を作成しました");
      setNewName("");
      await loadTenants();
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inviteMember = async () => {
    if (!invEmail.trim()) return;
    setLoading(true);
    try {
      await api.post(`/tenants/${activeTenantId}/members`, {
        email: invEmail, name: invName, role: "accountant", password: invPass || undefined,
        capabilities: invCaps,
      });
      toast.success("メンバーを追加しました");
      setInvEmail(""); setInvName(""); setInvPass(""); setInvRole("staff");
      setInvCaps({ accounting: true, billing: true, photo: true });
      await loadMembers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/tenants/${activeTenantId}/members/${uid}`, { role });
      toast.success("ロールを更新しました");
      await loadMembers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleCap = async (m, key) => {
    const current = m.capabilities || { accounting: true, billing: true, photo: true };
    const capabilities = { ...current, [key]: !current[key] };
    try {
      await api.patch(`/tenants/${activeTenantId}/members/${m.user_id}`, { capabilities });
      toast.success("権限を更新しました");
      await loadMembers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeMember = async (uid) => {
    try {
      await api.delete(`/tenants/${activeTenantId}/members/${uid}`);
      toast.success("メンバーを削除しました");
      await loadMembers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const stdLabel = (v) => STANDARDS.find((s) => s.value === v)?.label || v;
  const tabs = [
    { id: "companies", label: "会社" },
    { id: "members", label: "メンバー" },
    { id: "logs", label: "監査ログ" },
  ];

  return (
    <div className="max-w-4xl" data-testid="organization-page">
      <h1 className="font-display font-bold text-3xl text-stone-900">会社・メンバー</h1>
      <p className="mt-2 text-sm text-stone-500">会社（テナント）の作成、メンバーと権限、監査ログを管理します。</p>

      <div className="mt-6 flex gap-2 border-b border-stone-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`org-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-emerald-800 text-emerald-900" : "border-transparent text-stone-500 hover:text-stone-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "companies" && (
        <div className="mt-6 space-y-6">
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <div className="font-medium text-stone-900 flex items-center gap-2"><Plus className="h-4 w-4" />新しい会社を作成</div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <Label className="text-sm">会社名</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-company-name" className="mt-1.5" placeholder="株式会社〇〇" />
              </div>
              <div>
                <Label className="text-sm">会計基準</Label>
                <select value={newStd} onChange={(e) => setNewStd(e.target.value)} data-testid="new-company-standard" className="mt-1.5 w-full rounded-md border border-stone-200 px-2 py-2 text-sm">
                  {STANDARDS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <button onClick={createCompany} disabled={loading} data-testid="create-company-button" className="inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "作成"}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="companies-list">
            {tenants.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-emerald-700" />
                  <div>
                    <div className="font-medium text-stone-900">{t.name}{t.id === activeTenantId && <span className="ml-2 text-xs text-emerald-700">（利用中）</span>}</div>
                    <div className="text-xs text-stone-500">{stdLabel(t.accounting_standard)}</div>
                  </div>
                </div>
                <span className="text-xs rounded-full bg-stone-100 px-2.5 py-1 text-stone-600">{t.role_label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "members" && (
        <div className="mt-6 space-y-6">
          <div className="rounded-lg border border-stone-200 bg-white p-5">
            <div className="font-medium text-stone-900 flex items-center gap-2"><UserPlus className="h-4 w-4" />メンバーを追加</div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">メールアドレス</Label>
                <Input value={invEmail} onChange={(e) => setInvEmail(e.target.value)} data-testid="invite-email" className="mt-1.5" placeholder="member@example.com" />
              </div>
              <div>
                <Label className="text-sm">氏名</Label>
                <Input value={invName} onChange={(e) => setInvName(e.target.value)} data-testid="invite-name" className="mt-1.5" placeholder="山田 太郎" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-sm">権限（付与する機能）</Label>
                <div className="mt-1.5 flex flex-wrap gap-4">
                  {[
                    { k: "accounting", label: "会計権限" },
                    { k: "billing", label: "請求権限" },
                    { k: "photo", label: "写真入力権限" },
                  ].map((c) => (
                    <label key={c.k} className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!invCaps[c.k]}
                        onChange={(e) => setInvCaps((v) => ({ ...v, [c.k]: e.target.checked }))}
                        data-testid={`invite-cap-${c.k}`}
                        className="h-4 w-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm">初期パスワード（新規ユーザー時）</Label>
                <Input value={invPass} onChange={(e) => setInvPass(e.target.value)} data-testid="invite-password" className="mt-1.5" placeholder="未入力なら changeme123" />
              </div>
            </div>
            <button onClick={inviteMember} disabled={loading} data-testid="invite-button" className="mt-4 inline-flex items-center justify-center rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "追加する"}
            </button>
          </div>

          <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="members-list">
            {members.map((m) => {
              const adminRole = m.role === "company_admin" || m.role === "representative";
              const mcaps = m.capabilities || { accounting: true, billing: true, photo: true };
              return (
              <div key={m.user_id} className="flex flex-col sm:flex-row sm:items-center justify-between px-5 py-4 gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-stone-900 truncate">{m.name || m.email}</div>
                  <div className="text-xs text-stone-500 truncate">{m.email}</div>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {[
                      { k: "accounting", label: "会計" },
                      { k: "billing", label: "請求" },
                      { k: "photo", label: "写真入力" },
                    ].map((c) => (
                      <label key={c.k} className={`inline-flex items-center gap-1.5 text-xs ${adminRole ? "text-stone-400" : "text-stone-700 cursor-pointer"}`}>
                        <input
                          type="checkbox"
                          disabled={adminRole}
                          checked={adminRole ? true : !!mcaps[c.k]}
                          onChange={() => toggleCap(m, c.k)}
                          data-testid={`member-cap-${c.k}-${m.email}`}
                          className="h-3.5 w-3.5 rounded border-stone-300 text-emerald-700 focus:ring-emerald-600"
                        />
                        {c.label}
                      </label>
                    ))}
                    {adminRole && <span className="text-[11px] text-stone-400">（管理者は全権限）</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    value={m.role}
                    onChange={(e) => changeRole(m.user_id, e.target.value)}
                    data-testid={`member-role-${m.email}`}
                    className="rounded-md border border-stone-200 px-2 py-1.5 text-sm"
                  >
                    {roles.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                  </select>
                  {m.user_id !== user?.id && (
                    <button onClick={() => removeMember(m.user_id)} data-testid={`member-remove-${m.email}`} className="text-stone-400 hover:text-orange-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "logs" && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white divide-y divide-stone-100" data-testid="audit-logs-list">
          {logs.length === 0 && <div className="px-5 py-6 text-sm text-stone-500">ログはありません。</div>}
          {logs.map((l) => (
            <div key={l.id} className="px-5 py-3 text-sm flex items-center justify-between">
              <span className="text-stone-800"><b>{l.action}</b> — {l.target_type}</span>
              <span className="text-xs text-stone-400">{new Date(l.created_at).toLocaleString("ja-JP")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
